import { Link } from 'react-router-dom';
import { Users, Briefcase, Lock, ArrowRight, Rocket, Target } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';

interface ModuleConfig {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  iconBg: string;
  features: string[];
  comingSoon?: boolean;
}

const modules: ModuleConfig[] = [
  {
    id: 'itc',
    name: 'Information Technology Consulting',
    description: 'Du besoin client au positionnement : matching, qualification et réponse automatisés par IA',
    icon: Users,
    href: '/itc/candidates',
    iconBg: 'bg-bpa-accent',
    features: ['Matching automatique', 'Qualification CV par IA', 'Réponse au besoin', 'Relances planifiées'],
  },
  {
    id: 'tm',
    name: 'Tenders Management',
    description: 'Suivi des opportunités commerciales, de la détection à la contractualisation',
    icon: Briefcase,
    href: '/tm/opportunities',
    iconBg: 'bg-success',
    features: ['Import BOAMP/TED', 'Pipeline Kanban', 'Agents IA', 'Gestion des contrats'],
    comingSoon: true,
  },
  {
    id: 'ra',
    name: 'Recruitment Accelerator',
    description: 'Travail collaboratif augmenté avec une vision ATS pour piloter vos recrutements',
    icon: Rocket,
    href: '#',
    iconBg: 'bg-copper',
    features: ['Collective Work', 'Vision ATS', 'Pipeline partagé', 'KPIs recruteurs'],
    comingSoon: true,
  },
  {
    id: 'lg',
    name: 'Lead Generation',
    description: 'Stratégie inbound (dont CopyOps) et organisation outbound pour générer vos leads',
    icon: Target,
    href: '#',
    iconBg: 'bg-purple-600',
    features: ['Inbound & CopyOps', 'Organisation Outbound', 'Scoring leads', 'Reporting conversion'],
    comingSoon: true,
  },
];

export function DashboardPage() {
  const { user } = useAuth();

  // TODO: Récupérer les features depuis l'API tenant/subscription
  const enabledModules = ['itc', 'tm', 'ra', 'lg'];

  const isModuleEnabled = (moduleId: string) => enabledModules.includes(moduleId);

  return (
    <div className="space-y-10 animate-fade-in">
      {/* Header */}
      <div>
        <p className="overline mb-2">Dashboard</p>
        <h1 className="heading-1">
          Bonjour, {user?.firstName}
        </h1>
        <p className="text-[var(--color-text-secondary)] mt-3 text-lg">
          Bienvenue sur BPA Ecosystem. Sélectionnez une application pour commencer.
        </p>
      </div>

      {/* Modules Grid - 2 columns */}
      <div className="grid gap-6 md:grid-cols-2">
        {modules.map((module) => {
          const enabled = isModuleEnabled(module.id);
          const Icon = module.icon;

          return (
            <Card
              key={module.id}
              className={`relative overflow-hidden transition-all duration-200 border-[var(--color-border)] bg-surface-elevated flex flex-col ${
                enabled
                  ? 'hover:shadow-bpa-lg hover:border-[var(--color-border-strong)] cursor-pointer'
                  : 'cursor-not-allowed'
              }`}
            >
              {!enabled && (
                <div className="absolute inset-0 bg-surface/80 backdrop-blur-[2px] z-10 flex items-center justify-center">
                  <div className="text-center p-4">
                    <div className="h-12 w-12 mx-auto rounded-full bg-surface-sunken flex items-center justify-center mb-3">
                      <Lock className="h-5 w-5 text-[var(--color-text-tertiary)]" />
                    </div>
                    <p className="text-sm font-medium text-[var(--color-text-secondary)]">
                      Application non incluse
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-4 border-copper text-copper hover:bg-copper-muted"
                    >
                      Mettre à niveau
                    </Button>
                  </div>
                </div>
              )}

              <CardHeader className="pb-4">
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg ${module.iconBg}`}>
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-xl font-semibold text-ink">
                      {module.name}
                    </CardTitle>
                    <CardDescription className="mt-1 text-[var(--color-text-secondary)]">
                      {module.description}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="flex flex-col flex-1">
                <div className="flex flex-wrap gap-2 mb-5 flex-1">
                  {module.features.map((feature) => (
                    <span
                      key={feature}
                      className="inline-flex px-2.5 py-1 text-xs font-medium rounded-md bg-surface-sunken text-[var(--color-text-secondary)] h-fit"
                    >
                      {feature}
                    </span>
                  ))}
                </div>

                {enabled && !module.comingSoon && (
                  <Link to={module.href}>
                    <Button className="w-full bg-bpa-accent hover:bg-bpa-accent-hover text-surface">
                      Accéder à l'application
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                )}
                {enabled && module.comingSoon && (
                  <Button className="w-full" disabled>
                    Bientôt disponible
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
